import React, { useState, useEffect, useMemo } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { CopyIcon, CheckIcon } from '@phosphor-icons/react';
import { RootState, useAppSelector } from '../../../store';

export interface JsonInputFieldProps {
    value: string | object;
    onChange?: (value: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    minRows?: number;
    maxRows?: number;
    className?: string;
    label?: string;
    hint?: string;
    disabled?: boolean;
    readOnly?: boolean;
    showCopyButton?: boolean;
    onCopy?: (text: string) => void;
    'data-testid'?: string;
}

const JsonInputField: React.FC<JsonInputFieldProps> = ({
    value,
    onChange,
    onBlur,
    placeholder = '{}',
    minRows = 5,
    maxRows,
    className = '',
    label,
    hint,
    disabled = false,
    readOnly = false,
    showCopyButton = false,
    onCopy,
    'data-testid': testId,
}) => {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const stringValue = useMemo(() => {
        if (typeof value === 'string') return value;
        try {
            return JSON.stringify(value, null, 2);
        } catch {
            return String(value);
        }
    }, [value]);

    useEffect(() => {
        if (!stringValue.trim()) {
            setJsonError(null);
            return;
        }

        try {
            JSON.parse(stringValue);
            setJsonError(null);
        } catch (error) {
            setJsonError(
                i18n?.translate
                    ? i18n.translate({ id: 'error.invalidJson', sourceName: 'JsonInputField' }) || 'Invalid JSON format'
                    : 'Invalid JSON format',
            );
        }
    }, [stringValue, i18n]);

    const handleBlur = () => {
        if (!jsonError && stringValue.trim() && onChange) {
            try {
                const parsed = JSON.parse(stringValue);
                const formatted = JSON.stringify(parsed, null, 2);
                if (formatted !== stringValue) {
                    onChange(formatted);
                }
            } catch {}
        }
        onBlur?.();
    };

    const handleCopy = async () => {
        try {
            if (onCopy) {
                onCopy(stringValue);
            } else {
                await navigator.clipboard.writeText(stringValue);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            try {
                const textArea = document.createElement('textarea');
                textArea.value = stringValue;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            } catch {}
        }
    };

    const isEditable = !readOnly && !disabled && onChange;

    return (
        <div className={className}>
            {label && <label className="block mb-2 text-layout-textcolor">{label}</label>}
            <div className="flex flex-col gap-3">
                <div className="relative">
                    {showCopyButton && (
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="absolute top-2 right-2 z-10 p-1 text-layout-textcolor hover:text-primary transition-colors"
                            title="Copy JSON"
                        >
                            {copied ? <CheckIcon className="w-4 h-4" /> : <CopyIcon className="w-4 h-4" />}
                        </button>
                    )}
                    <TextareaAutosize
                        className={`bg-input-backgroundColor w-full p-3 border border-2 ${
                            jsonError ? 'border-red-500' : 'border-input-borderColor'
                        } rounded-lg outline-none focus:border-primary transition-all duration-100 ${
                            !isEditable ? 'bg-gray-50 cursor-default' : ''
                        }`}
                        value={stringValue}
                        onChange={isEditable ? (e) => onChange!(e.target.value) : undefined}
                        onBlur={isEditable ? handleBlur : undefined}
                        placeholder={placeholder}
                        minRows={minRows}
                        maxRows={maxRows}
                        disabled={disabled}
                        readOnly={readOnly || !onChange}
                        data-testid={testId}
                        spellCheck={false}
                        style={{
                            fontFamily:
                                'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                            fontSize: '14px',
                            lineHeight: '1.4',
                            resize: 'vertical',
                            whiteSpace: 'nowrap',
                            overflowX: 'auto',
                        }}
                    />
                </div>
                {jsonError && <p className="text-sm text-red-500">{jsonError}</p>}
                {hint && !jsonError && <p className="text-sm text-gray-500">{hint}</p>}
            </div>
        </div>
    );
};

export default JsonInputField;
